import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import usePricing from '../hooks/usePricing';
import './ConfirmBooking.css';

export default function ConfirmBooking() {
  const location = useLocation();
  const { packages: pkgArr } = usePricing();

  // Build a quick lookup map from the live pricing { trial: { label, upfront }, … }
  const PACKAGES = pkgArr.reduce((acc, p) => {
    acc[p.id] = { label: p.label, upfront: p.upfront };
    return acc;
  }, {});

  // page states: 'loading' | 'ready' | 'redirecting' | 'alreadyPaid' | 'invalid' | 'payError'
  const [state,    setState]    = useState('loading');
  const [booking,  setBooking]  = useState(null);
  const [token,    setToken]    = useState(null);
  const [payError, setPayError] = useState('');
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get('token');
    const wasCancelled = params.get('cancelled') === '1';
    if (wasCancelled) setCancelled(true);

    if (!t) { setState('invalid'); return; }
    setToken(t);

    fetch(`/api/bookings/confirm-info?token=${encodeURIComponent(t)}`)
      .then(r => r.json())
      .then(data => {
        if (data.valid) {
          setBooking(data);
          setState('ready');
        } else if (data.alreadyPaid) {
          setState('alreadyPaid');
        } else {
          setState('invalid');
        }
      })
      .catch(() => setState('invalid'));
  }, [location.search]);

  const handlePay = async () => {
    setState('redirecting');
    try {
      const res = await fetch('/api/payments/create-checkout-from-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const json = await res.json();
      if (json.url) {
        window.location.href = json.url;
      } else {
        setPayError(json.error || 'Something went wrong. Please try again or contact us.');
        setState('payError');
      }
    } catch {
      setPayError('Network error. Please check your connection and try again.');
      setState('payError');
    }
  };

  /* ── Loading ─────────────────────────────────────────────────── */
  if (state === 'loading') {
    return (
      <div className="cb-page">
        <div className="cb-card cb-card--center">
          <div className="cb-spinner" />
          <p>Verifying your booking link…</p>
        </div>
      </div>
    );
  }

  /* ── Invalid link ────────────────────────────────────────────── */
  if (state === 'invalid') {
    return (
      <div className="cb-page">
        <div className="cb-card cb-card--center">
          <div className="cb-icon cb-icon--warn">✕</div>
          <h2>Invalid or Expired Link</h2>
          <p>This booking link isn't valid or has already expired. If you think this is an error, please contact us.</p>
          <Link to="/contact" className="cb-btn cb-btn--outline">Contact Us</Link>
        </div>
      </div>
    );
  }

  /* ── Already paid ────────────────────────────────────────────── */
  if (state === 'alreadyPaid') {
    return (
      <div className="cb-page">
        <div className="cb-card cb-card--center">
          <div className="cb-icon cb-icon--ok">✓</div>
          <h2>Booking Already Confirmed</h2>
          <p>This booking has already been paid and confirmed. You should have received a confirmation email with your instructor's details.</p>
          <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Didn't receive your email? <Link to="/contact" style={{ color: '#16a34a' }}>Contact us</Link></p>
        </div>
      </div>
    );
  }

  /* ── Pay error ───────────────────────────────────────────────── */
  if (state === 'payError') {
    return (
      <div className="cb-page">
        <div className="cb-card cb-card--center">
          <div className="cb-icon cb-icon--warn">!</div>
          <h2>Payment Setup Failed</h2>
          <p>{payError}</p>
          <button className="cb-btn cb-btn--primary" onClick={() => { setState('ready'); setCancelled(false); }}>
            Try Again
          </button>
          <Link to="/contact" className="cb-btn cb-btn--outline">Contact Us</Link>
        </div>
      </div>
    );
  }

  /* ── Redirecting ─────────────────────────────────────────────── */
  if (state === 'redirecting') {
    return (
      <div className="cb-page">
        <div className="cb-card cb-card--center">
          <div className="cb-spinner" />
          <p>Taking you to secure checkout…</p>
        </div>
      </div>
    );
  }

  /* ── Ready (main state) ──────────────────────────────────────── */
  const pkg = PACKAGES[booking?.packageType] || {
    label: booking?.packageLabel || 'Package',
    upfront: booking?.upfront || 0,
  };

  return (
    <div className="cb-page">
      {/* Hero */}
      <div className="cb-hero">
        <div className="cb-hero-inner">
          {/* Trust badges */}
          <div className="cb-hero-badges">
            {['🇬🇧 DVSA-Approved', '✓ Fully Insured', '✓ DBS Checked', '🔒 Secure Checkout'].map(badge => (
              <span key={badge} className="cb-hero-badge">{badge}</span>
            ))}
          </div>

          <h1 className="cb-hero-title">
            Your Instructor Is Ready —<br />
            <span className="cb-hero-title-accent">Confirm &amp; Pay</span>
          </h1>
          <p className="cb-hero-sub">
            Almost there! Review your booking details below and complete the secure upfront payment to lock in your spot.
          </p>

          {/* Step progress */}
          <div className="cb-steps">
            {[
              { label: 'Request Sent',        done: true  },
              { label: 'Instructor Assigned', done: true  },
              { label: 'Confirm & Pay',       done: false, active: true },
            ].map((step, i) => (
              <React.Fragment key={step.label}>
                {i > 0 && (
                  <div className={`cb-step-line${step.done ? ' cb-step-line--done' : ''}`} />
                )}
                <div className="cb-step">
                  <div className={[
                    'cb-step-circle',
                    step.done   ? 'cb-step-circle--done'   : '',
                    step.active ? 'cb-step-circle--active' : '',
                  ].join(' ').trim()}>
                    {step.done ? '✓' : i + 1}
                  </div>
                  <span className={`cb-step-label${step.active ? ' cb-step-label--active' : ''}`}>
                    {step.label}
                  </span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <div className="cb-layout">
        {/* Main card */}
        <div className="cb-main">

          {/* Cancelled banner */}
          {cancelled && (
            <div className="cb-banner cb-banner--warn">
              <span>⚠️</span>
              <div>
                <strong>Payment not completed.</strong> Your booking is still reserved — use the button below to try again.
              </div>
            </div>
          )}

          {/* Instructor confirmed badge */}
          <div className="cb-confirmed-badge">
            <span className="cb-confirmed-icon">✓</span>
            <div>
              <p className="cb-confirmed-label">Instructor Confirmed</p>
              <p className="cb-confirmed-name">{booking?.instructorFirstName || 'Your instructor'} is ready for you!</p>
            </div>
          </div>

          {/* Booking summary */}
          <div className="cb-summary">
            <h3>Booking Summary</h3>
            <ul className="cb-summary-list">
              <li><span>Student</span><strong>{booking?.studentName}</strong></li>
              <li><span>Package</span><strong>{booking?.packageLabel || pkg.label}</strong></li>
              <li><span>Transmission</span><strong>{booking?.transmission || '—'}</strong></li>
              <li><span>Test Centre</span><strong>{booking?.testCentre || '—'}</strong></li>
              <li><span>Instructor</span><strong>{booking?.instructorFirstName || '—'}</strong></li>
            </ul>
          </div>

          {/* Upfront fee */}
          <div className="cb-fee-box">
            <p className="cb-fee-label">Upfront Booking Fee</p>
            <p className="cb-fee-amount">£{booking?.upfront ?? pkg.upfront}</p>
            <p className="cb-fee-note">Remaining balance paid directly to your instructor after lessons begin.</p>
          </div>

          {/* Pay button */}
          <button className="cb-pay-btn" onClick={handlePay}>
            ✓ Confirm &amp; Pay £{booking?.upfront ?? pkg.upfront} →
          </button>

          <p className="cb-secure-note">🔒 Secure checkout powered by Stripe. We never store your card details.</p>
        </div>

        {/* Sidebar */}
        <aside className="cb-sidebar">
          <div className="cb-sidebar-card">
            <h4>How it works</h4>
            <ol className="cb-how-list">
              <li><strong>Pay now</strong> — secure your spot with the upfront fee</li>
              <li><strong>Meet your instructor</strong> — full contact details emailed instantly</li>
              <li><strong>Remaining balance</strong> — paid directly to your instructor</li>
            </ol>
          </div>
          <div className="cb-sidebar-card">
            <h4>Need help?</h4>
            <p style={{ fontSize: '0.9rem', color: '#64748b', margin: '0.5rem 0 1rem' }}>If you have any questions about your booking, get in touch.</p>
            <Link to="/contact" className="cb-btn cb-btn--outline cb-btn--full">Contact Us</Link>
          </div>
          <div className="cb-sidebar-card cb-sidebar-card--trust">
            <span>✓ DVSA-Approved Instructors</span>
            <span>✓ Fully Insured</span>
            <span>✓ DBS Checked</span>
            <span>✓ Secure Stripe Checkout</span>
          </div>
        </aside>
      </div>
    </div>
  );
}
